import { useMemo, useState } from 'react'
import { Cell, Column } from 'react-table'
import { format } from 'date-fns'
import { Button, Text, Badge, useDisclosure } from '@ritualco/jutsu'
import { BasicTable } from 'components/BasicTable'
import { useContent } from 'lib/content/react'
import { ExternalConnectionsTableRow } from 'types/entities'
import { ConnectionDeleteModal } from 'components/Modals/ConnectionDeleteModal'

export type ConnectionsTableProps = {
  connections?: ExternalConnectionsTableRow[]
  onRemove: (connectionId: string) => void
}

export const ConnectionsTable = ({
  connections = [],
  onRemove,
}: ConnectionsTableProps) => {
  const { formatMessage } = useContent()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [deleteProps, setDeleteProps] = useState({})
  const columns = useMemo<Column<ExternalConnectionsTableRow>[]>(
    () => [
      {
        accessor: 'id',
        Header: formatMessage({ id: 'pmportal.connections.workspace' }),
        Cell: ({ row }: Cell<ExternalConnectionsTableRow>) => (
          <>
            <Text fontWeight="medium">{row.original.organizationName}</Text>
            <Text fontSize="xs" color="grayscale.secondary">
              {row.original.workspaceName}
            </Text>
          </>
        ),
      },
      {
        id: 'connectionDate',
        accessor: ({ connectionDate }) =>
          connectionDate ? format(connectionDate, 'MMMM d, yyyy, p') : '-',
        Header: formatMessage({ id: 'pmportal.connections.connectionDate' }),
      },
      {
        id: 'status',
        accessor: ({ status }) => {
          if (status === 'REVOKED' || status === 'PENDING') {
            return (
              <Badge colorScheme="red" variant="outline">
                {formatMessage({ id: 'pmportal.connections.deletedStatus' })}
              </Badge>
            )
          } else {
            return (
              <Badge>
                {formatMessage({ id: 'pmportal.connections.acceptedStatus' })}
              </Badge>
            )
          }
        },
        Header: formatMessage({ id: 'pmportal.connections.status' }),
      },
      {
        id: 'actions',
        Cell: ({ row }: Cell<ExternalConnectionsTableRow>) => {
          const showDelete =
            row.original.status !== 'REVOKED' &&
            row.original.status !== 'PENDING'
          return (
            showDelete && (
              <Button
                size="sm"
                colorScheme="transparent"
                variant="outline"
                float="right"
                onClick={() => {
                  setDeleteProps({
                    organizationName: row.original.organizationName,
                    workspaceName: row.original.workspaceName,
                    onConfirm: () => {
                      onRemove(row.original.id)
                    },
                  })
                  onOpen()
                }}
              >
                {formatMessage({ id: 'pmportal.global.actionRemove' })}
              </Button>
            )
          )
        },
      },
    ],
    [formatMessage, onOpen, onRemove]
  )

  return (
    <>
      <BasicTable
        columns={columns}
        data={connections}
        placeholder={formatMessage({ id: 'pmportal.connections.emptyTable' })}
        tdProps={() => ({ lineHeight: 'inherit' })}
      />
      <ConnectionDeleteModal
        isOpen={isOpen}
        onClose={onClose}
        {...deleteProps}
      />
    </>
  )
}
